import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { FormlyConfig, FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { FormStateService } from '../form-state.service';
import { AddServiceModalComponent } from './add-service-modal/add-service-modal.component';
import { debounceTime } from 'rxjs/operators';
import { ServiceProviderService } from '../../../../Service/Service-providers/service-provider.service';
import { ToastService } from '../../../../Service/ToastService/toast.service';
import { CustomCheckboxComponent } from '../../custom-checkbox';

@Component({
  selector: 'app-service-offered-new',
  templateUrl: './service-offered-new.component.html',
  styleUrls: ['./service-offered-new.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceOfferedNewComponent implements OnInit {
  @ViewChild(AddServiceModalComponent)
  addServiceModal!: AddServiceModalComponent;
  form: FormGroup;
  // model: any = {};
  model: any = {
    service_: '',
  };
  options: FormlyFormOptions = {};
  fields: FormlyFieldConfig[] = [];
  serviceLists: any[] = [];
  filteredServices: any[] = [];
  showModal: boolean = false;
  serviceNameToAdd: string = '';
  selectedBusinessType: any;
  @Output() nextSection = new EventEmitter<void>();
  searchQuery = '';
  private isUpdatingFields = false;
  receivedObject: any;

  constructor(
    private formStateService: FormStateService,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef,
    private api: ServiceProviderService,
    private toastService: ToastService,
    private formlyConfig: FormlyConfig

  ) {
    this.form = this.formBuilder.group({
      selectedCategory: [''],
    });
  }

  ngOnInit(): void {
    if (!this.formlyConfig.getType('custom-checkbox')) {
      this.formlyConfig.setType({
        name: 'custom-checkbox',
        component: CustomCheckboxComponent,
      });
    }
    this.formStateService
      .getBusinessTypes()
      .subscribe((allBusinessTypes: any[]) => {
        this.serviceLists = allBusinessTypes.map((businessType: any) => ({
          label: businessType.name,
          value: businessType.businessTypeId,
          services: businessType.services,
        }));

        this.formStateService.selectedBusinessType$.subscribe(
          (businessType: any) => {
            if (businessType) {
              // Reset the selected services whenever the business type changes
              this.resetSelectedServices();

              this.selectedBusinessType = businessType;
              this.patchFormValue({
                selectedCategory: {
                  label: businessType.name,
                  value: businessType.businessTypeId,
                },
              });
              this.updateServicesByCategory(businessType.services);
            }
          }
        );
        this.loadFormData();
      });

    this.form.valueChanges.pipe(debounceTime(300)).subscribe((value) => {
      if (this.isUpdatingFields) return;

      this.isUpdatingFields = true;

      try {
        this.formStateService.setFormData('service-offered', value);
        const selectedCategory = value.selectedCategory;
        if (selectedCategory && selectedCategory.value) {
          const businessType = this.serviceLists.find(
            (bt) => bt.value === selectedCategory.value
          );
          this.formStateService.setSelectedBusinessType(businessType);
          this.updateServicesByCategory(businessType.services);
          if (this.receivedObject && this.receivedObject.serviceOffers) {
            this.receivedObject.serviceOffers.forEach((offer: any) => {
              if (
                businessType.services.some(
                  (s: any) => s.serviceId === offer.serviceId
                )
              ) {
                this.model[`service_${offer.serviceId}`] = true;
              }
            });
          }
          this.updateFields();
        }
      } finally {
        this.isUpdatingFields = false;
      }
    });
  }

  private patchFormValue(value: any): void {
    this.isUpdatingFields = true;
    this.form.patchValue(value);
    this.isUpdatingFields = false;
  }

  updateServicesByCategory(services: any[]): void {
    this.filteredServices = services.map((service) => ({
      id: service.serviceId || service.id,
      label: service.name || service.label,
    }));
    this.updateFields();
  }

  updateFields(): void {
    if (this.isUpdatingFields) return;
    this.isUpdatingFields = true;

    // Maintain the current state of selected checkboxes
    const selectedServices = Object.keys(this.model)
      .filter((key) => key.startsWith('service_') && this.model[key])
      .map((key) => Number(key.replace('service_', ''))); // Convert to number

    if (this.filteredServices.length === 0 && this.searchQuery) {
      this.fields = [
        {
          key: 'add_new',
          type: 'custom-add-box',
          templateOptions: {
            label: `Add "${this.searchQuery}"`,
            customClass: 'add-new-service',
            onClick: () => this.onAddNewService(this.searchQuery),
          },
        },
      ];
    } else {
      this.fields = this.filteredServices.map((service) => {
        const serviceId = service.id || service.serviceId;
        return {
          key: 'service_' + serviceId,
          type: 'custom-checkbox',
          templateOptions: {
            label: service.label || service.name,
          },
          defaultValue: this.model['service_' + service.id] || false, // Retrieve saved checkbox state
        };
      });

      // Patch the form values to maintain checkbox states
      const patchValues: any = {};
      this.filteredServices.forEach((service) => {
        const serviceId = service.id || service.serviceId;
        patchValues['service_' + serviceId] =
          selectedServices.includes(serviceId);
      });

      this.form.patchValue(patchValues, { emitEvent: false });
    }

    setTimeout(() => {
      this.cdr.detectChanges();
      this.isUpdatingFields = false;
    });
  }

  onSubmit() {
    if (this.form.valid) {
      const selectedServiceIds = Object.keys(this.form.value)
        .filter((key) => key.startsWith('service_') && this.form.value[key])
        .map((key) => Number(key.replace('service_', ''))); // Convert to number

      this.formStateService.setServices({
        serviceOfferedIds: selectedServiceIds,
      });

      const combinedData = this.formStateService.getCombinedData();
      combinedData.serviceOfferedIds = selectedServiceIds;
      console.log('Combined Form Data:', combinedData);
      this.formStateService.setFormData('combined', combinedData);
      this.nextSection.emit();
    }
  }

  resetForm() {
    this.form.reset();
    this.model = {};
  }

  onSearchChange(event: Event): void {
    this.searchQuery = (event.target as HTMLInputElement)?.value.toLowerCase();

    if (this.searchQuery) {
      const selectedCategory = this.form.get('selectedCategory')?.value;
      if (selectedCategory && selectedCategory.value) {
        const businessType = this.serviceLists.find(
          (bt) => bt.value === selectedCategory.value
        );
        if (businessType) {
          this.filteredServices = businessType.services.filter((service: any) =>
            service.name.toLowerCase().includes(this.searchQuery)
          );
        }
      } else {
        this.filteredServices = [];
      }
    } else {
      const selectedCategory = this.form.get('selectedCategory')?.value;
      if (selectedCategory && selectedCategory.value) {
        const businessType = this.serviceLists.find(
          (bt) => bt.value === selectedCategory.value
        );
        if (businessType) {
          this.filteredServices = [...businessType.services];
        }
      } else {
        this.filteredServices = [];
      }
    }
    this.updateFields();
  }

  onAddNewService(serviceName: string): void {
    this.serviceNameToAdd = serviceName;
    this.showModal = true;
  }

  onConfirmAddService(confirmed: boolean): void {
    this.showModal = false;
    if (confirmed) {
      const serviceName =
        this.serviceNameToAdd.charAt(0).toUpperCase() +
        this.serviceNameToAdd.slice(1);

      const data = {
        name: serviceName,
        businessTypeId: this.selectedBusinessType.businessTypeId,
      };
      console.log(data);
      this.api.postNewService(data).subscribe(
        (response) => {
          this.toastService.showToast('Service added successfully!', 'success');
          const newService = {
            serviceId: response.serviceId,
            name: serviceName,
          };
          this.selectedBusinessType.services.push(newService);

          const businessTypeIndex = this.serviceLists.findIndex(
            (bt) => bt.value === this.selectedBusinessType.businessTypeId
          );
          if (businessTypeIndex !== -1) {
            this.serviceLists[businessTypeIndex].services =
              this.selectedBusinessType.services;
          }

          this.searchQuery = '';
          this.filteredServices = [...this.selectedBusinessType.services];
          this.updateFields();
        },
        (error) => {
          this.toastService.showToast(
            'Failed to add service. Please try again.',
            'error'
          );
        }
      );
    }
  }

  loadFormData() {
    const savedData = this.formStateService.getFormData('service-offered');
    if (savedData) {
      this.model = savedData;

      // Convert saved service IDs to numbers before patching the form
      const patchValues: any = {};
      Object.keys(savedData).forEach((key) => {
        if (key.startsWith('service_')) {
          patchValues[key] = Number(savedData[key]);
        }
      });

      this.form.patchValue(savedData);
    }
  }

  // Clear the selected services from the model
  private resetSelectedServices(): void {
    Object.keys(this.model)
      .filter((key) => key.startsWith('service_'))
      .forEach((key) => {
        this.model[key] = false; // Resest selected service to false
      });
    this.form.patchValue(this.model, { emitEvent: false });

    this.cdr.detectChanges();
  }

  populateFormWithServiceProviderDetails(res: any) {
    //  Patching the selected business type in (selectedCategory)
    if (res.businessType) {
      this.form.patchValue({
        selectedCategory: {
          label: res.businessType.name,
          value: res.businessType.businessTypeId,
        },
      });

      //Update services for the selected business type
      const businessType = this.serviceLists.find(
        (bt) => bt.value === res.businessType.businessTypeId
      );

      if (businessType) {
        this.updateServicesByCategory(businessType.services);
      }
    }

    //Patch the preselected services in the model
    if (res.serviceOffers && res.serviceOffers.length > 0) {
      res.serviceOffers.forEach((offer: any) => {
        // Set the checkbox for the service as true for (preselected)
        this.model[`service_${offer.serviceId}`] = true;
      });
    }

    //Patching the form with the updated model
    this.form.patchValue(this.model);
    this.cdr.detectChanges();

    //Setting the updated form data to the formStateService
    this.formStateService.setFormData('service-offered', this.model);
  }

}
